import {
  type Day,
  type POI,
  type ScheduledPOI,
  type TransportEvent,
  type ReorderItem,
  type ResolvedDayItem,
  type LocationRef,
  type BaseLocation,
} from '@poi-planner/shared';
import { parseTimeToMinutes, minutesToTimeString } from './format';
import { estimateTravel, type Coords, type TravelEstimate } from './geo';

// ── Time-Based Reordering ──

export interface ReorderResult {
  scheduledPOIs: ScheduledPOI[];
  transportEvents: TransportEvent[];
}

/**
 * Reorders day items chronologically based on their times.
 *
 * Algorithm:
 * 1. Merge POIs and transport events into a single list
 * 2. For each item, determine its "sort time":
 *    - POI with startTime: use startTime
 *    - Transport: use departureTime (required)
 *    - POI without startTime: cascade from wake + prior items
 * 3. Sort by sort time (stable - preserve relative order on ties)
 *    - Transport before POI at same time (you arrive, then do activity)
 * 4. Assign new sequential order values (0, 1, 2, ...)
 */
export function reorderDayItemsByTime(
  day: Day,
  transportEvents: TransportEvent[],
  pois: POI[]
): ReorderResult {
  const poiMap = new Map(pois.map(p => [p.id, p]));
  const dayTransport = transportEvents.filter(te => te.dayId === day.id);

  // Build list of items with their properties
  const items: ReorderItem[] = [];

  for (const sp of day.scheduledPOIs) {
    const poi = poiMap.get(sp.poiId);
    items.push({
      type: 'poi',
      id: sp.poiId,
      explicitTime: sp.startTime ? parseTimeToMinutes(sp.startTime) : null,
      originalOrder: sp.order,
      poiTimeBudget: poi?.timeBudget ?? 60,
    });
  }

  for (const te of dayTransport) {
    items.push({
      type: 'transport',
      id: te.id,
      explicitTime: parseTimeToMinutes(te.departureTime),
      originalOrder: te.order,
      transportDuration: te.duration,
    });
  }

  // If no items or all items are untimed POIs, no reordering needed
  const hasTimedItems = items.some(it => it.explicitTime !== null);
  if (items.length === 0 || !hasTimedItems) {
    return {
      scheduledPOIs: day.scheduledPOIs,
      transportEvents: dayTransport,
    };
  }

  // Calculate effective times for items without explicit times
  // First pass: sort by original order to compute cascading times
  items.sort((a, b) => a.originalOrder - b.originalOrder);

  const effectiveTimes = new Map<string, number>();
  let currentTime = parseTimeToMinutes(day.wakeUpTime);

  for (const item of items) {
    if (item.explicitTime !== null) {
      effectiveTimes.set(item.id, item.explicitTime);
      currentTime = item.explicitTime;
    } else {
      effectiveTimes.set(item.id, currentTime);
    }
    // Advance time by item duration
    if (item.type === 'poi') {
      currentTime += item.poiTimeBudget ?? 60;
    } else {
      currentTime += item.transportDuration ?? 0;
    }
  }

  // Sort by effective time, with tiebreakers:
  // 1. Transport before POI at same time
  // 2. Original order for same type at same time
  items.sort((a, b) => {
    const timeA = effectiveTimes.get(a.id) ?? 0;
    const timeB = effectiveTimes.get(b.id) ?? 0;

    if (timeA !== timeB) return timeA - timeB;

    // Same time: transport before POI
    if (a.type !== b.type) {
      return a.type === 'transport' ? -1 : 1;
    }

    // Same type, same time: preserve original order
    return a.originalOrder - b.originalOrder;
  });

  // Assign new sequential orders
  const newScheduledPOIs: ScheduledPOI[] = [];
  const newTransportEvents: TransportEvent[] = [];

  let order = 0;
  for (const item of items) {
    if (item.type === 'poi') {
      const sp = day.scheduledPOIs.find(s => s.poiId === item.id);
      if (sp) {
        newScheduledPOIs.push({ ...sp, order });
      }
    } else {
      const te = dayTransport.find(t => t.id === item.id);
      if (te) {
        newTransportEvents.push({ ...te, order });
      }
    }
    order++;
  }

  // Return transport events not in this day unchanged
  const otherTransport = transportEvents.filter(te => te.dayId !== day.id);

  return {
    scheduledPOIs: newScheduledPOIs,
    transportEvents: [...otherTransport, ...newTransportEvents],
  };
}

/**
 * Computes what time a POI would have at a given order position in the day.
 * Used when dragging POIs to set their start time based on drop position.
 * Includes travel time estimates between POIs.
 */
export function computeTimeAtPosition(
  day: Day,
  transportEvents: TransportEvent[],
  pois: POI[],
  targetOrder: number,
  wakeAccommodation?: BaseLocation
): string {
  const poiMap = new Map(pois.map(p => [p.id, p]));
  const dayTransport = transportEvents.filter(te => te.dayId === day.id);

  // Build merged sorted items (before inserting the new one)
  type Item = {
    order: number;
    time: number | null;
    duration: number;
    coords: Coords | null;
    isTransport: boolean;
  };
  const items: Item[] = [];

  for (const sp of day.scheduledPOIs) {
    const poi = poiMap.get(sp.poiId);
    items.push({
      order: sp.order,
      time: sp.startTime ? parseTimeToMinutes(sp.startTime) : null,
      duration: poi?.timeBudget ?? 60,
      coords: getCoords(poi),
      isTransport: false,
    });
  }

  for (const te of dayTransport) {
    items.push({
      order: te.order,
      time: parseTimeToMinutes(te.departureTime),
      duration: te.duration,
      coords: null, // Transport breaks coord chain
      isTransport: true,
    });
  }

  // Sort by order
  items.sort((a, b) => a.order - b.order);

  // Walk through items before target position to compute the time
  let currentTime = parseTimeToMinutes(day.wakeUpTime);
  let currentCoords: Coords | null = getCoords(wakeAccommodation);

  for (const item of items) {
    if (item.order >= targetOrder) break;

    // Add travel time to this item if we have coords
    if (!item.isTransport && currentCoords && item.coords) {
      const travel = estimateTravel(currentCoords, item.coords);
      if (travel) {
        currentTime += travel.travelMinutes;
      }
    }

    if (item.time !== null) {
      currentTime = item.time;
    }

    currentTime += item.duration;

    // Update current coords (transport breaks the chain)
    if (item.isTransport) {
      currentCoords = null;
    } else {
      currentCoords = item.coords;
    }
  }

  // Add travel time from last item to the target position
  // (This accounts for travel to reach the drop position)
  // We don't have the target POI's coords here, so we just return the time
  // at the end of the previous item

  return minutesToTimeString(currentTime);
}

export interface TravelSegment {
  fromLabel: string;
  toLabel: string;
  estimate: TravelEstimate | null;
}

export interface TimeOverrun {
  poiId: string;        // POI causing overrun
  nextPoiId: string;    // POI with manual start time
  overrunMinutes: number;
}

export interface AutoTimeResult {
  computedStartTimes: Map<string, string>;
  travelSegments: TravelSegment[];
  totalTravelMinutes: number;
  timeOverruns: TimeOverrun[];
}

function getCoords(obj: { lat: number | null; lng: number | null } | undefined | null): Coords | null {
  if (!obj || obj.lat == null || obj.lng == null) return null;
  return { lat: obj.lat, lng: obj.lng };
}

// Fallback when location reference can't be resolved
const UNKNOWN_LOCATION: BaseLocation = {
  id: '',
  name: 'Unknown',
  lat: null,
  lng: null,
  notes: null,
};

/**
 * Resolves a LocationRef to a BaseLocation (POI or Accommodation-tagged POI).
 * Handles legacy format (originPoiId/destinationPoiId) for unmigrated data.
 * Since accommodations are now POIs with "accommodation" tag, both ref types
 * look up from the same maps.
 */
function resolveLocationRef(
  ref: LocationRef | undefined,
  poiMap: Map<string, POI>,
  accommodationPoiMap: Map<string, POI>,
  legacyPoiId?: string // Fallback for unmigrated transport events
): BaseLocation {
  // Handle undefined ref (unmigrated data) - fall back to legacy POI lookup
  if (!ref) {
    if (legacyPoiId) {
      return poiMap.get(legacyPoiId) ?? accommodationPoiMap.get(legacyPoiId) ?? UNKNOWN_LOCATION;
    }
    return UNKNOWN_LOCATION;
  }

  // Both 'poi' and 'accommodation' types now point to POIs
  // Try accommodation map first for 'accommodation' type, then fall back to regular POIs
  const location = ref.type === 'accommodation'
    ? (accommodationPoiMap.get(ref.id) ?? poiMap.get(ref.id))
    : (poiMap.get(ref.id) ?? accommodationPoiMap.get(ref.id));
  return location ?? UNKNOWN_LOCATION;
}

/**
 * Computes auto-times for all POIs in a day.
 * @param wakeAccommodation - POI with "accommodation" tag where day starts (previous day's sleep)
 * @param sleepAccommodation - POI with "accommodation" tag where day ends
 * @param accommodationPOIs - All POIs with "accommodation" tag (for resolving LocationRefs)
 */
export function computeDayAutoTimes(
  day: Day,
  pois: POI[],
  transportEvents: TransportEvent[],
  wakeAccommodation: BaseLocation | undefined,
  sleepAccommodation: BaseLocation | undefined,
  accommodationPOIs: POI[] = [],
): AutoTimeResult {
  const computedStartTimes = new Map<string, string>();
  const travelSegments: TravelSegment[] = [];
  const timeOverruns: TimeOverrun[] = [];
  let totalTravelMinutes = 0;

  const poiMap = new Map(pois.map((p) => [p.id, p]));
  const accommodationPoiMap = new Map(accommodationPOIs.map((a) => [a.id, a]));

  // Build merged sorted items
  const sortedScheduledPOIs = [...day.scheduledPOIs].sort((a, b) => a.order - b.order);
  const dayTransport = transportEvents.filter((te) => te.dayId === day.id);

  const items: ResolvedDayItem[] = [
    ...sortedScheduledPOIs.map((sp): ResolvedDayItem | null => {
      const poi = poiMap.get(sp.poiId);
      if (!poi) return null;
      return { type: 'poi', poi, scheduledPOI: sp };
    }).filter((x): x is ResolvedDayItem => x !== null),
    ...dayTransport.map((te): ResolvedDayItem => ({ type: 'transport', event: te })),
  ].sort((a, b) => {
    const orderA = a.type === 'poi' ? a.scheduledPOI.order : a.event.order;
    const orderB = b.type === 'poi' ? b.scheduledPOI.order : b.event.order;
    return orderA - orderB;
  });

  let currentTime = parseTimeToMinutes(day.wakeUpTime);
  let currentCoords: Coords | null = getCoords(wakeAccommodation);
  const wakeLabel = wakeAccommodation?.name ?? 'Start';

  // Process each item
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    let fromLabel = wakeLabel;
    if (i > 0) {
      const prev = items[i - 1];
      if (prev.type === 'poi') {
        fromLabel = prev.poi.name;
      } else {
        const prevOrigin = resolveLocationRef(prev.event.origin, poiMap, accommodationPoiMap, (prev.event as any).originPoiId);
        const prevDest = resolveLocationRef(prev.event.destination, poiMap, accommodationPoiMap, (prev.event as any).destinationPoiId);
        fromLabel = `${prevOrigin.name}→${prevDest.name}`;
      }
    }

    if (item.type === 'poi') {
      const itemCoords = getCoords(item.poi);
      let estimate: TravelEstimate | null = null;

      if (currentCoords && itemCoords) {
        estimate = estimateTravel(currentCoords, itemCoords);
        if (estimate) {
          totalTravelMinutes += estimate.travelMinutes;
          currentTime += estimate.travelMinutes;
        }
      }

      travelSegments.push({
        fromLabel,
        toLabel: item.poi.name,
        estimate,
      });

      if (item.scheduledPOI.startTime) {
        // Manual override — use as anchor
        currentTime = parseTimeToMinutes(item.scheduledPOI.startTime);
      } else {
        // Store computed time
        computedStartTimes.set(item.poi.id, minutesToTimeString(currentTime));
      }

      currentTime += item.poi.timeBudget;
      currentCoords = itemCoords;

      // Check for overrun: look ahead to see if next POI has manual start time
      const nextItem = items[i + 1];
      if (nextItem && nextItem.type === 'poi' && nextItem.scheduledPOI.startTime) {
        const nextCoords = getCoords(nextItem.poi);
        let travelToNext = 0;
        if (itemCoords && nextCoords) {
          const travelEstimate = estimateTravel(itemCoords, nextCoords);
          if (travelEstimate) {
            travelToNext = travelEstimate.travelMinutes;
          }
        }
        const effectiveEndTime = currentTime + travelToNext;
        const nextManualStart = parseTimeToMinutes(nextItem.scheduledPOI.startTime);
        if (effectiveEndTime > nextManualStart) {
          timeOverruns.push({
            poiId: item.poi.id,
            nextPoiId: nextItem.poi.id,
            overrunMinutes: effectiveEndTime - nextManualStart,
          });
        }
      }

    } else {
      // Transport event — resolve origin/destination from LocationRef
      const originResolved = resolveLocationRef(item.event.origin, poiMap, accommodationPoiMap, (item.event as any).originPoiId);
      const destResolved = resolveLocationRef(item.event.destination, poiMap, accommodationPoiMap, (item.event as any).destinationPoiId);
      const originCoords = getCoords(originResolved);
      const destCoords = getCoords(destResolved);

      // Calculate travel from current position to transport origin
      let travelToOrigin: TravelEstimate | null = null;
      if (currentCoords && originCoords) {
        travelToOrigin = estimateTravel(currentCoords, originCoords);
        if (travelToOrigin) {
          totalTravelMinutes += travelToOrigin.travelMinutes;
          currentTime += travelToOrigin.travelMinutes;
        }
      }

      travelSegments.push({
        fromLabel,
        toLabel: `${originResolved.name}→${destResolved.name}`,
        estimate: travelToOrigin,
      });

      if (item.event.departureTime) {
        currentTime = parseTimeToMinutes(item.event.departureTime);
      }
      currentTime += item.event.duration;

      // Continue coordinate chain from destination (KEY: don't break the chain)
      currentCoords = destCoords;
    }
  }

  // Final segment: last item → sleep accommodation
  const sleepCoords = getCoords(sleepAccommodation);
  let lastLabel = wakeLabel;
  if (items.length > 0) {
    const last = items[items.length - 1];
    if (last.type === 'poi') {
      lastLabel = last.poi.name;
    } else {
      const lastOrigin = resolveLocationRef(last.event.origin, poiMap, accommodationPoiMap, (last.event as any).originPoiId);
      const lastDest = resolveLocationRef(last.event.destination, poiMap, accommodationPoiMap, (last.event as any).destinationPoiId);
      lastLabel = `${lastOrigin.name}→${lastDest.name}`;
    }
  }
  const sleepLabel = sleepAccommodation?.name ?? 'End';

  let finalEstimate: TravelEstimate | null = null;
  if (currentCoords && sleepCoords) {
    finalEstimate = estimateTravel(currentCoords, sleepCoords);
    if (finalEstimate) {
      totalTravelMinutes += finalEstimate.travelMinutes;
    }
  }

  travelSegments.push({
    fromLabel: lastLabel,
    toLabel: sleepLabel,
    estimate: finalEstimate,
  });

  return { computedStartTimes, travelSegments, totalTravelMinutes, timeOverruns };
}
